import {useMemo, useState} from "react";
import moment from "moment";
import {FormGroup} from "react-bootstrap";
import {Alert, Loading, NoResultsDiv} from "fearless-common-ui";
import {LoggedUIEvent, User} from "fearless-common";
import useGet from "../../../components/hooks/useGet";
import {backendEndpoints} from "../../../libs/backendEndpoints";
import Description from "../../../components/Description";
import {databaseDateTimeFormat, databaseDateFormat} from "../../../utils/date";

/**
 * Displays a sorted bar-chart table of logged UI event counts grouped by tag
 * for a specific user, with a date range filter defaulting to the past three months.
 */
export default function AdminUserLoggedUiEvents({ userId }: {
  userId: User['id']
}) {
  const [fromDate, setFromDate] = useState(() => moment().subtract(3, 'months').format(databaseDateFormat));

  const params = useMemo(() => ({
    user_id: userId,
    created_at: `>=${moment(fromDate).startOf('day').format(databaseDateTimeFormat)}`,
  }), [userId, fromDate]);

  const { data, isLoading, error } = useGet<LoggedUIEvent[]>({
    endpoint: backendEndpoints.getLoggedUiEvents,
    params,
  });

  const sortedTagCounts = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const counts: Record<string, number> = {};
    data.forEach(({ tag }) => {
      counts[tag] = (counts[tag] ?? 0) + 1;
    });
    return Object.entries(counts).sort(([, a], [, b]) => b - a);
  }, [data]);

  const maxCount = sortedTagCounts[0]?.[1] ?? 1;

  return (
    <>
      <h6>Logged UI events</h6>
      <Description className="text-sm line-height-1-0">
        Logged UI events are registered events from any of our applications. E.g. button clicks, keyboard shortcuts, etc.
        Useful to understand how a user interact with our applications, so we can approach them with relevant X-Y-Z.
      </Description>
      <form className="d-flex gap-2 mb-2">
        <FormGroup className="d-flex align-items-center gap-2">
          <label className="text-sm">From</label>
          <input type="date" className="form-control form-control-sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </FormGroup>
      </form>
      {isLoading && <Loading />}
      {error && <Alert>Error</Alert>}
      {!error && !isLoading && Array.isArray(sortedTagCounts) && sortedTagCounts.length === 0 && (
        <NoResultsDiv className="text-sm">No logged UI events found. Events are saved in batches, so the batch may not be saved to our database yet</NoResultsDiv>
      )}
      <table className="table table-sm text-xs">
        <tbody>
          {sortedTagCounts.map(([tag, count]) => (
            <tr key={tag}>
              <td className="text-nowrap neutral-600 align-middle" style={{ width: 180 }}>{tag}</td>
              <td className="align-middle">
                <div className="bg-light rounded" style={{ height: 12 }}>
                  <div
                    className="bg-primary rounded"
                    style={{ width: `${(count / maxCount) * 100}%`, height: '100%' }}
                  />
                </div>
              </td>
              <td className="text-nowrap fw-bold align-middle text-end" style={{ width: 30 }}>{count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
